/**
 * EVO — vozvrat (RMA / QC / utilizatsiya) ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/sales/returns.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/replenishment.ts` bilan
 * bir xil naqsh). Bu qo'lda saqlanadigan shartnoma: server ustunni qayta
 * nomlasa TypeScript uni ushlamaydi, shuning uchun har o'zgarish jonli
 * tekshiriladi.
 *
 * ============================================================================
 * MIQDOR VA PUL — `string`, VA BU YERDA AYNIQSA MUHIM
 * ============================================================================
 * `parseFloat` TAQIQLANGAN (`lib/format.ts` fayl boshidagi qoida). Bu ekranda
 * raqam ikki tomonga ta'sir qiladi: mijozga qaytariladigan PUL va ombordan
 * chiqadigan TOVAR. Oxirgi xonadagi xato mijoz bilan nizoga aylanadi va uni
 * keyin dalil bilan tuzatib bo'lmaydi.
 *
 * ============================================================================
 * LUG'ATLAR SHU YERDA TAKRORLANADI — VA BU ATAYLAB
 * ============================================================================
 * Har taqdirning ekranda o'z rangi, o'z yorlig'i va o'z IZOHI bor. Server
 * yangi taqdir qo'shsa va u bu ro'yxatga kiritilsa, ekrandagi
 * `Record<ReturnDisposition, …>` to'liq bo'lmagani uchun **kompilyatsiya
 * yiqiladi** — ya'ni yangi taqdir jimgina «noma'lum» bo'lib qolmaydi
 * (`lib/replenishment.ts` `REORDER_LINE_FLAGS` bilan bir xil mulohaza).
 */
import { api } from './api';
import type { Page, SortDir } from './products';

/** `packages/db/src/schema/sales.ts` → `SALES_RETURN_STATUS`. */
export const RETURN_STATUSES = [
  'draft',
  'approved',
  'received',
  'inspected',
  'settled',
  'cancelled',
] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

/** `RETURN_REASON_CODES`. `qcRequired` standarti shundan chiqadi. */
export const RETURN_REASONS = [
  'defect',
  'damaged',
  'warranty',
  'wrong_item',
  'customer_change',
  'other',
] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

/** `RETURN_SETTLEMENT_TYPES`. */
export const RETURN_SETTLEMENTS = ['credit_note', 'refund', 'replacement'] as const;
export type ReturnSettlement = (typeof RETURN_SETTLEMENTS)[number];

/** `RETURN_DISPOSITIONS` — qaytgan tovar bilan NIMA QILINADI. */
export const RETURN_DISPOSITIONS = [
  'resale',
  'repair',
  'replacement',
  'supplier_claim',
  'quarantine',
  'scrap',
] as const;
export type ReturnDisposition = (typeof RETURN_DISPOSITIONS)[number];

/** `QC_RESULTS`. */
export const QC_RESULTS = ['pass', 'fail', 'conditional'] as const;
export type QcResult = (typeof QC_RESULTS)[number];

/** `returns.support.ts` → `RETURN_DEFECT_REASONS`. */
export const DEFECT_REASONS = [
  'burnt',
  'not_working',
  'low_output',
  'flicker',
  'physical_damage',
  'packaging_damage',
  'wrong_spec',
  'incomplete',
  'other',
] as const;
export type DefectReason = (typeof DEFECT_REASONS)[number];

/**
 * Ko'rik SABABDAN kelib chiqadi — server `qcRequiredFor()` bilan AYNAN bir xil.
 *
 * Bu takror EMAS, balki forma odamga «nima bo'lishini» OLDINDAN ko'rsatishi
 * uchun: sabab tanlanganda ekranda darhol «karantinga tushadi» deb yozilishi
 * kerak. Server baribir o'zi hal qiladi va uning qarori yakuniy — bu yerdagi
 * qiymat faqat KO'RSATMA.
 */
const QC_REQUIRED_BY_REASON: Record<ReturnReason, boolean> = {
  defect: true,
  damaged: true,
  warranty: true,
  wrong_item: false,
  customer_change: false,
  other: false,
};

export function qcRequiredHint(reason: ReturnReason): boolean {
  return QC_REQUIRED_BY_REASON[reason];
}

/**
 * Taqdir ombor harakatiga olib keladimi — tugma matnini tanlash uchun.
 *
 * Serverdagi `movementFor()` ning ekran uchun qisqartirilgan ko'rinishi:
 * odamga «bosganimda tovar joyidan qimirlaydimi?» degan savolga javob kerak.
 */
const DISPOSITION_MOVES: Record<ReturnDisposition, boolean> = {
  resale: true,
  scrap: true,
  repair: false,
  replacement: false,
  supplier_claim: false,
  quarantine: false,
};

export function dispositionMoves(disposition: ReturnDisposition, quarantined: boolean): boolean {
  if (disposition === 'resale' && !quarantined) return false;
  return DISPOSITION_MOVES[disposition];
}

// ---------------------------------------------------------------------------
// Javob shakllari
// ---------------------------------------------------------------------------

export interface ReturnLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineAmount: string;
  readonly invoiceLineId: number | null;
  readonly inventoryTxnId: number | null;
  readonly qcResult: QcResult | null;
  readonly defectReason: string | null;
  readonly dispositionCode: ReturnDisposition;
  readonly dispositionTxnId: number | null;
  readonly dispositionAt: string | null;
  readonly dispositionBy: number | null;
  readonly notes: string | null;
  /** Faqat `product.cost.view` — sotuvchida bu maydon KELMAYDI. */
  readonly unitCost?: string;
}

export interface ReturnDetail {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly invoiceId: number | null;
  readonly invoiceNo: string | null;
  readonly warehouseId: number;
  readonly warehouseName: string;
  readonly quarantineWarehouseId: number | null;
  readonly quarantineWarehouseName: string | null;
  readonly qcRequired: boolean;
  readonly returnDate: string;
  readonly reasonCode: ReturnReason;
  readonly settlementType: ReturnSettlement;
  readonly totalAmount: string;
  readonly status: ReturnStatus;
  readonly notes: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: number | null;
  readonly closedAt: string | null;
  readonly lines: readonly ReturnLine[];
  /**
   * Bloklamaydigan ogohlantirishlar: tannarx topilmadi, narx e'tiborga
   * olinmadi, pul harakati qo'lda rasmiylashtiriladi. Ular EKRANDA
   * ko'rsatilishi SHART — server ularni aynan shuning uchun qaytaradi.
   */
  readonly warnings: readonly string[];
}

export interface ReturnListItem {
  readonly id: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly warehouseId: number;
  readonly quarantineWarehouseId: number | null;
  readonly qcRequired: boolean;
  readonly returnDate: string;
  readonly reasonCode: ReturnReason;
  readonly settlementType: ReturnSettlement;
  readonly totalAmount: string;
  readonly status: ReturnStatus;
  readonly lineCount: number;
}

export interface QuarantineItem {
  readonly lineId: number;
  readonly returnId: number;
  readonly docNo: string;
  readonly customerId: number;
  readonly customerName: string;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  readonly warehouseId: number | null;
  readonly warehouseName: string | null;
  readonly qcResult: QcResult | null;
  readonly defectReason: string | null;
  readonly dispositionCode: ReturnDisposition;
  readonly returnDate: string;
  readonly receivedAt: string | null;
  readonly daysInQuarantine: number;
  readonly status: ReturnStatus;
}

// ---------------------------------------------------------------------------
// So'rov parametrlari
// ---------------------------------------------------------------------------

export interface ReturnListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly status?: ReturnStatus | undefined;
  readonly customerId?: number | undefined;
  readonly reasonCode?: ReturnReason | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly q?: string | undefined;
}

export interface QuarantineParams {
  readonly page: number;
  readonly pageSize: number;
  readonly dispositionCode?: ReturnDisposition | undefined;
  readonly qcResult?: QcResult | undefined;
  readonly minDays?: number | undefined;
  readonly q?: string | undefined;
}

export interface CreateReturnLineBody {
  readonly productId: number;
  readonly quantity: string;
  readonly invoiceLineId?: number;
  readonly unitPrice?: string;
  readonly notes?: string | null;
}

export interface CreateReturnBody {
  readonly customerId: number;
  readonly warehouseId: number;
  readonly reasonCode: ReturnReason;
  readonly invoiceId?: number;
  readonly returnDate?: string;
  readonly settlementType?: ReturnSettlement;
  readonly qcRequired?: boolean;
  readonly quarantineWarehouseId?: number;
  readonly notes?: string | null;
  readonly lines: readonly CreateReturnLineBody[];
}

export interface QcLineBody {
  readonly qcResult: QcResult;
  readonly defectReason?: DefectReason | null;
  readonly dispositionCode?: ReturnDisposition;
  readonly notes?: string | null;
}

export const returnKeys = {
  all: ['returns'] as const,
  list: (params: ReturnListParams) => ['returns', 'list', params] as const,
  detail: (id: number) => ['returns', 'detail', id] as const,
  quarantine: (params: QuarantineParams) => ['returns', 'quarantine', params] as const,
};

function listQuery(params: ReturnListParams): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('pageSize', String(params.pageSize));
  if (params.sortBy) search.set('sortBy', params.sortBy);
  search.set('sortDir', params.sortDir);
  if (params.status) search.set('status', params.status);
  if (params.reasonCode) search.set('reasonCode', params.reasonCode);
  if (params.customerId !== undefined) search.set('customerId', String(params.customerId));
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.q) search.set('q', params.q);
  return search.toString();
}

function quarantineQuery(params: QuarantineParams): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('pageSize', String(params.pageSize));
  if (params.dispositionCode) search.set('dispositionCode', params.dispositionCode);
  if (params.qcResult) search.set('qcResult', params.qcResult);
  if (params.minDays !== undefined) search.set('minDays', String(params.minDays));
  if (params.q) search.set('q', params.q);
  return search.toString();
}

export function fetchReturns(
  params: ReturnListParams,
  signal?: AbortSignal,
): Promise<Page<ReturnListItem>> {
  return api.get<Page<ReturnListItem>>(`/returns?${listQuery(params)}`, { signal });
}

export function fetchReturn(id: number, signal?: AbortSignal): Promise<ReturnDetail> {
  return api.get<ReturnDetail>(`/returns/${id}`, { signal });
}

export function fetchQuarantine(
  params: QuarantineParams,
  signal?: AbortSignal,
): Promise<Page<QuarantineItem>> {
  return api.get<Page<QuarantineItem>>(`/returns/quarantine?${quarantineQuery(params)}`, { signal });
}

export function createReturn(body: CreateReturnBody): Promise<ReturnDetail> {
  return api.post<ReturnDetail>('/returns', body);
}

export function approveReturn(id: number): Promise<ReturnDetail> {
  return api.post<ReturnDetail>(`/returns/${id}/approve`, {});
}

export function receiveReturn(id: number): Promise<ReturnDetail> {
  return api.post<ReturnDetail>(`/returns/${id}/receive`, {});
}

export function qcReturnLine(id: number, lineId: number, body: QcLineBody): Promise<ReturnDetail> {
  return api.put<ReturnDetail>(`/returns/${id}/lines/${lineId}/qc`, body);
}

export function disposeReturn(id: number, lineIds?: readonly number[]): Promise<ReturnDetail> {
  return api.post<ReturnDetail>(
    `/returns/${id}/dispose`,
    lineIds === undefined ? {} : { lineIds: [...lineIds] },
  );
}

export function settleReturn(id: number, settlementType?: ReturnSettlement): Promise<ReturnDetail> {
  return api.post<ReturnDetail>(
    `/returns/${id}/settle`,
    settlementType === undefined ? {} : { settlementType },
  );
}

export function cancelReturn(id: number, reason: string): Promise<ReturnDetail> {
  return api.post<ReturnDetail>(`/returns/${id}/cancel`, { reason });
}
